import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { WoocommerceCart } from "../types/Cart.ts";
import { Woocommerce } from "../types/Order.ts";
import {
  AddToCartRequest,
  ClearCartRequest,
  UpdateOrderBundleRequest,
  WordPressProduct,
} from "../types/Product.ts";
import { SpecsTableData, WordPressProductSpecs } from "../types/SpecsTable.ts";
import { Vehicle, WordPressVehicle } from "../types/Vehicle";

const baseHeaders: HeadersInit = import.meta.env.VITE_IMPERSONATE_ROLE
  ? {
      "X-Force-Role": import.meta.env.VITE_IMPERSONATE_ROLE,
    }
  : {};

export const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_JMACX_WORDPRESS_URL + "/wp-json",
  headers: baseHeaders,
});

const vehicleResponseTransformer = (
  response: WordPressVehicle[],
): Vehicle[] => {
  // Exclude all vehicles that should be hidden from the GVM builder
  const vehicles = response.filter(
    (vehicle) => !vehicle.acf.hide_in_gvm_builder,
  );

  const allVehicles: Record<number, Vehicle> = {};

  // Start with the root vehicles (those with no parent - WordPress gives them a parent of 0)
  let ids = [0];

  // There's no guarantee that the vehicles will be returned in any particular order,
  // so we need to start with the 'root' vehicles (those with no parent) and go from there.
  //
  // Each iteration of this loop will go one level deeper into the tree of vehicles.
  while (true) {
    const leaves = vehicles.filter((vehicle) => ids.includes(vehicle.parent));

    // No more leaves means we've reached the maximum depth of the tree.
    if (leaves.length === 0) {
      break;
    }

    // Keep track of the children IDs - they'll be used in the next iteration to find the next set of vehicles.
    const newIds: number[] = [];

    for (const vehicle of leaves) {
      const parent = allVehicles[vehicle.parent];

      const newVehicle: Vehicle = {
        ...vehicle,
        children: [],
        parents: parent ? [...parent.parents, parent.id] : [],
      };

      if (parent) {
        parent.children.push(newVehicle);
      }

      allVehicles[vehicle.id] = newVehicle;
      newIds.push(vehicle.id);
    }

    ids = newIds;
  }

  return Object.values(allVehicles);
};

const gvmUpgradeResponseTransformer = (upgrades: WordPressProduct[]) => {
  // Filter out unsupported products
  upgrades = upgrades.filter(
    (upgrade) => upgrade.extensions?.cc_complex_bundle,
  );

  for (const upgrade of upgrades) {
    for (const [
      stepIndex,
      step,
    ] of upgrade.extensions.cc_complex_bundle.steps.entries()) {
      // Transform step title: "Optional Selections" -> "Variable Selections"
      if (step.step_name === "Optional Selections") {
        step.step_name = "Variable Selections";
      }

      for (const [entryIndex, entry] of step.entries.entries()) {
        for (const [optionIndex, option] of entry.products.entries()) {
          if (option.option_name) {
            option.option_name = option.option_name.replace(/<br\/>/g, " - ");
          }
          option.key = `${stepIndex}-${entryIndex}-${optionIndex}`;
        }
      }
    }
  }

  return upgrades;
};

async function getNonce(baseQuery: any): Promise<string | undefined> {
  // Nonce is needed for any POST requests, so grab one from the GET cart endpoint.
  const cart = await baseQuery("/wc/store/v1/cart");

  if (cart.error || !cart.meta?.response) {
    return cart;
  }

  return cart.meta.response.headers.get("nonce") || undefined;
}

export const wordPressApi = createApi({
  reducerPath: "wordPressApi",
  baseQuery,
  tagTypes: ["Vehicles", "GvmUpgrades", "Cart", "Order"],
  endpoints: (builder) => ({
    getVehicles: builder.query<Vehicle[], void>({
      query: () => "/wp/v2/vehicle_make_cat?per_page=100&acf_format=standard",
      providesTags: ["Vehicles"],
      keepUnusedDataFor: 1800,
      transformResponse: vehicleResponseTransformer,
    }),

    getGvmUpgrades: builder.query<WordPressProduct[], void>({
      query: () => "/wc/store/r6/gvm-upgrades",
      providesTags: ["GvmUpgrades"],
      keepUnusedDataFor: 10,
      transformResponse: gvmUpgradeResponseTransformer,
    }),

    getCart: builder.query<WoocommerceCart, void>({
      query: () => "/wc/store/v1/cart",
      providesTags: ["Cart"],
    }),

    addToCart: builder.mutation<unknown, AddToCartRequest>({
      async queryFn(data, _queryApi, _extraOptions, baseQuery) {
        const nonce = data.nonce || (await getNonce(baseQuery));

        return baseQuery({
          url: "/wc/store/r6/cart/complex-bundle",
          method: "POST",
          body: data,
          headers: {
            ...baseHeaders,
            Nonce: nonce || "",
          },
        });
      },
    }),

    clearCart: builder.mutation<unknown, ClearCartRequest>({
      async queryFn(data, _queryApi, _extraOptions, baseQuery) {
        const nonce = data.nonce || (await getNonce(baseQuery));

        return baseQuery({
          url: "/wc/store/v1/cart/items",
          method: "DELETE",
          body: data,
          headers: {
            ...baseHeaders,
            Nonce: nonce || "",
          },
        });
      },
    }),

    getOrder: builder.query<Woocommerce.Order, number>({
      query: (id) => `/wc/store/v1/order/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Order", id }],
    }),

    getSpecsTable: builder.query<SpecsTableData | null, number>({
      query: (id) => `/wp/v2/product/${id}`,
      transformResponse: (data: WordPressProductSpecs) => data.acf ?? null,
    }),

    updateOrderBundle: builder.mutation<unknown, UpdateOrderBundleRequest>({
      async queryFn(data, _queryApi, _extraOptions, baseQuery) {
        const nonce = data.nonce || (await getNonce(baseQuery));

        return baseQuery({
          url: `/wc/store/r6/order/complex-bundle/${data.order_id}`,
          method: "PUT",
          body: data,
          headers: {
            ...baseHeaders,
            Nonce: nonce || "",
          },
        });
      },
    }),
  }),
});

export interface ParsedError {
  code: string;
  message: string;
  data?: any;
}

export const parseError = (error: any): ParsedError => {
  if (error instanceof Error) {
    return {
      code: "js_error",
      message: error.message ?? "A JavaScript error occurred",
      data: error,
    };
  }

  if (error.data?.code && error.data?.message) {
    return {
      code: error.data.code,
      message: error.data.message,
      data: error,
    };
  }

  if (error.status === "PARSING_ERROR") {
    return {
      code: "parsing_error",
      message: "Failed to parse the response from the server",
      data: error,
    };
  }

  return {
    code: "unknown_error",
    message: "An unknown error occurred",
    data: error,
  };
};

export const {
  useGetCartQuery,
  useLazyGetCartQuery,
  useGetVehiclesQuery,
  useGetGvmUpgradesQuery,
  useAddToCartMutation,
  useClearCartMutation,
  useGetOrderQuery,
  useGetSpecsTableQuery,
  useUpdateOrderBundleMutation,
} = wordPressApi;
